import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteWorkflow } from '@/lib/api/generated/api';
import { invalidateWorkspace } from '@/features/workspaces';
import { expectStatus } from '@/lib/api/client';

/** The 409 codes this dialog knows; anything else is a refusal it has no copy for. */
const KNOWN_CONFLICTS = new Set([
  'WORKFLOW_HAS_RUNNING_TASKS',
  'WORKFLOW_LINKED_FROM',
]);

/**
 * What the dialog says when the service refuses because tasks on the workflow are
 * running (spec 146 FR-004). Deleting under a run would lose the usage it records
 * when it ends, so nothing is deleted until they stop.
 *
 * With no usable count (`undefined`: details missing or not a positive integer)
 * it says the plural without a number — the safe default, since the service only
 * refuses when at least one task is running.
 */
function runningTasksRefusal(running: number | undefined): string {
  if (running === undefined) return 'Stop its running tasks first.';
  if (running === 1) return 'Stop its running task first.';
  return `Stop its ${String(running)} running tasks first.`;
}

/**
 * `details.running`, if it is what the contract promises. The schema types
 * `details` only as a map of integers, so the shape is checked here rather than
 * trusted.
 */
function runningCount(
  details: Record<string, unknown> | null | undefined,
): number | undefined {
  const running = details?.running;
  return typeof running === 'number' && Number.isInteger(running) && running > 0
    ? running
    : undefined;
}

/**
 * Say that a 409 came back shaped unlike the contract. The person still sees a
 * refusal; this is for whoever has to find out why.
 */
function reportOddConflict(problem: string, body: unknown): void {
  console.warn(`delete workflow: ${problem}`, body);
}

/**
 * Permanently deletes a workflow and everything positioned on it. Success
 * invalidates the workspace aggregate, which is what removes the workflow
 * (and its tasks) from every surface reading that aggregate.
 */
export function useDeleteWorkflow(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workflowId: string): Promise<void> => {
      const response = await deleteWorkflow(workspaceId, workflowId);
      // The 409 is shared with the linked-from refusal (spec 108); the envelope's
      // code tells them apart. Running tasks get the dialog's own copy; every
      // other 409 — linked-from, or a code this client does not know yet — shows
      // the service's `message`, and only falls back to the generic copy below
      // when there is none.
      if (response.status === 409) {
        const { error, message, details } = response.data;
        if (error === 'WORKFLOW_HAS_RUNNING_TASKS') {
          const running = runningCount(details);
          if (running === undefined) {
            reportOddConflict(
              'running-tasks refusal without a count',
              response.data,
            );
          }
          throw new Error(runningTasksRefusal(running));
        }
        if (!KNOWN_CONFLICTS.has(error)) {
          reportOddConflict(`unrecognised 409 code ${error}`, response.data);
        }
        if (message) throw new Error(message);
      }
      expectStatus(response, 204, 'The workflow could not be deleted.');
    },
    onSuccess: () => invalidateWorkspace(queryClient, workspaceId),
  });
}
